import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Building2, Home as HomeIcon, Landmark, Layers } from 'lucide-react';
import { siteData } from '../mock/data';
import ScrollReveal, { ImageReveal } from '../components/ScrollReveal';
import ContactBlock from '../components/ContactBlock';

const icons = [Building2, HomeIcon, Landmark, Layers];

const Objects = () => {
  const { objects } = siteData;
  return (
    <>
      {/* HERO */}
      <section className="pt-40 pb-24">
        <div className="container-x">
          <div className="font-mono-up text-[#0A2A28]/60 mb-8 hero-eyebrow">
            <span className="gold-line" />{objects.eyebrow}
          </div>
          <h1 className="font-display text-[12vw] md:text-[8.5vw] leading-[0.9] text-[#0A2A28] -tracking-[0.02em]">
            <span className="hero-title block" dangerouslySetInnerHTML={{ __html: objects.title }} />
            <span className="hero-title-2 block font-serif-italic text-[#BFA070]">{objects.titleAccent}</span>
          </h1>
          <div className="grid md:grid-cols-12 gap-10 mt-14 hero-para">
            <div className="md:col-span-6 md:col-start-6">
              <p className="text-[#0A2A28]/80 text-lg leading-relaxed">{objects.intro}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Big image */}
      <section className="pb-24">
        <div className="container-x">
          <ImageReveal src={objects.bigImage} alt="Immobilienportfolio" className="aspect-[16/9]" />
        </div>
      </section>

      {/* Portfolio categories */}
      <section className="section bg-[#EFE9DE]">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-6">
              <ScrollReveal>
                <div className="font-mono-up text-[#0A2A28]/60 mb-6">
                  <span className="gold-line" />Bestände die wir verwalten
                </div>
              </ScrollReveal>
              <ScrollReveal delay={1}>
                <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#0A2A28]">
                  Portfolio mit <span className="font-serif-italic text-[#BFA070]">Charakter.</span>
                </h2>
              </ScrollReveal>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-[1px] bg-[#0A2A28]/10">
            {objects.portfolio.map((p, i) => {
              const Icon = icons[i] || Building2;
              return (
                <ScrollReveal key={p.title} delay={(i % 2) + 1}>
                  <div className="bg-[#EFE9DE] p-10 md:p-14 group hover:bg-[#F5F1EA] transition-colors duration-500 h-full">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 border border-[#0A2A28]/20 flex items-center justify-center text-[#0A2A28] group-hover:bg-[#0A2A28] group-hover:text-[#BFA070] transition-all duration-500 flex-shrink-0">
                        <Icon size={26} strokeWidth={1.25} />
                      </div>
                      <div>
                        <div className="lux-num mb-3">0{i + 1}</div>
                        <h3 className="font-display text-3xl md:text-4xl leading-tight text-[#0A2A28] mb-4 group-hover:text-[#BFA070] transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-[#0A2A28]/70 leading-relaxed">{p.text}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eigentümer */}
      <section className="section dark-section radial-glow">
        <div className="container-x grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-6">
            <ImageReveal src={objects.teamImage} alt="Eigentümer" className="aspect-[4/5]" />
          </div>
          <div className="md:col-span-6">
            <ScrollReveal>
              <div className="font-mono-up text-[#BFA070] mb-6">
                <span className="gold-line" />Eigentümer mit Weitblick
              </div>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-[#E8E2D5] mb-8">
                Immobilien verantwortungsvoll <span className="font-serif-italic text-[#BFA070]">steuern.</span>
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <p className="text-[#E8E2D5]/80 text-lg leading-relaxed mb-8">
                Aurelian Heights begleitet Eigentümer mit Immobilienportfolios, ebenso wie Investoren, Vermögensverwalter und Privatanleger. Wir sorgen dafür, dass Luxusimmobilien professionell gesteuert werden, Abläufe transparent bleiben und Entwicklungen nachhaltig begleitet werden.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={3}>
              <ul className="space-y-3 mb-10">
                {['Family Offices', 'Institutionelle Investoren', 'Vermögensverwalter', 'Privatanleger'].map((v) => (
                  <li key={v} className="flex items-center gap-3 text-[#E8E2D5]/85">
                    <Check size={18} strokeWidth={1.5} className="text-[#BFA070]" />
                    <span className="text-lg">{v}</span>
                  </li>
                ))}
              </ul>
              <Link to="/kontakt" className="lux-btn lux-btn-light">
                <span>Beratung anfragen</span>
                <ArrowUpRight className="arrow" size={16} strokeWidth={1.5} />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Reference list */}
      <section className="section">
        <div className="container-x">
          <ScrollReveal>
            <div className="font-mono-up text-[#0A2A28]/60 mb-6">
              <span className="gold-line" />Ausgewählte Referenzen
            </div>
          </ScrollReveal>
          <ScrollReveal delay={1}>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#0A2A28] mb-16">
              Objekte in <span className="font-serif-italic text-[#BFA070]">Betreuung.</span>
            </h2>
          </ScrollReveal>
          <div className="border-t border-[#0A2A28]/15">
            {objects.references.map((r, i) => (
              <ScrollReveal key={r.name} delay={(i % 3) + 1}>
                <div className="grid grid-cols-12 py-8 md:py-10 border-b border-[#0A2A28]/15 group hover:bg-[#EFE9DE]/60 transition-colors duration-500 cursor-pointer items-center">
                  <div className="col-span-1 lux-num">0{i + 1}</div>
                  <div className="col-span-11 md:col-span-5 font-display text-2xl md:text-4xl text-[#0A2A28] group-hover:text-[#BFA070] transition-colors">{r.name}</div>
                  <div className="hidden md:block md:col-span-2 font-mono-up text-[#0A2A28]/60">{r.type}</div>
                  <div className="hidden md:block md:col-span-2 font-mono-up text-[#0A2A28]/60">{r.area}</div>
                  <div className="col-span-12 md:col-span-2 flex justify-end items-center gap-2 font-mono-up text-[#0A2A28]/70 mt-3 md:mt-0">
                    <span>{r.city}</span>
                    <ArrowUpRight size={16} strokeWidth={1.5} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <ContactBlock />
    </>
  );
};

export default Objects;
